import * as types from './dataIntegration.types';
import { Dispatch } from 'react';
import { notificationErr } from 'views/components/notification';
import { dataIntegrationError } from 'utils/errorCodes';
import { ITranslate } from 'interfaces/general.interface';
import {
  createAudienceAPI,
  createFilePatternAPI,
  createMappingFieldAPI,
  createPointsOfSaleDictionaryAPI,
  deleteMappingFieldAPI,
  deletePointsOfSaleDictionaryAPI,
  getAudiencesAPI,
  getFilePatternsAPI,
  getMappingFieldsAPI,
  getMappingsAPI,
  getPointsOfSaleDictionaryAPI,
  updateAudienceAPI,
  updateMappingFieldAPI,
  updatePointsOfSaleDictionaryAPI,
  deleteMapAPI,
  createMappingAPI,
  updateMappingAPI,
  uploadFilesAPI,
  uploadFileToS3API,
  confirmUploadFileAPI,
  runDataPipelineAPI,
  getHistoryAPI,
  reprocessImportAPI,
  updateInboxFileEncodingAPI,
  archiveFileHistoryAPI,
} from 'api/endpoints/dataIntegration/dataIntegration.api';
import { TdataIntegrationDispatchReducer } from './dataIntegration.reducer';
import {
  IcreateAudienceAPIBody,
  IcreateFilePatternAPIBody,
  IcreateMappingAPIBody,
  IcreateMappingFieldAPIBodyItem,
  IcreatePointsOfSaleDictionaryAPIBodyItem,
  IgetHistoryAPIBody,
  IgetHistoryAPIResponseData,
  IupdateAudienceAPIBody,
  IupdateMappingAPIBody,
  IupdateMappingFieldAPIBody,
  IupdatePointsOfSaleDictionaryAPIBody,
  IuploadFilesAPIBodyItem,
  TgetHistoryAPIResponseItemStates,
} from 'api/endpoints/dataIntegration/dataIntegration.response.interface';
import { messageError, messageSuccess, messageWarning } from 'views/components/message';
import { IInitialDataIntegrationState } from 'interfaces/dataIntegration.interface';
import { initialUploadsHistoryBody } from '../initialDataIntegrationState';

// History
export async function getImportFilesHistoryAction({
  dispatch,
  importFilesHistoryBody,
  brandId,
  token,
  translate,
  runDataPipeline,
  audience,
  state,
  search,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  importFilesHistoryBody: IgetHistoryAPIBody;
  brandId: string;
  token: string;
  translate: ITranslate;
  runDataPipeline?: boolean;
  audience: string | null;
  state?: TgetHistoryAPIResponseItemStates | null;
  search?: string | null;
}): Promise<boolean> {
  try {
    importFilesHistoryBody.brand_id = brandId;
    // if (!runDataPipeline) {
    //   importFilesHistoryBody.index = 0;
    // }
    if (audience) {
      importFilesHistoryBody.audience = audience;
    } else {
      delete importFilesHistoryBody.audience;
    }

    if (state) {
      importFilesHistoryBody.state = state;
    } else {
      delete importFilesHistoryBody.state;
    }

    if (search) {
      importFilesHistoryBody.search = search;
    } else {
      delete importFilesHistoryBody.search;
    }

    const getHistory = await getUploadsHistory({
      dispatch,
      importFilesHistoryBody,
      token,
    });
    if (!getHistory) {
      notificationErr(dataIntegrationError.getUploadsHistory_001, translate);
      return false;
    }
    return true;
  } catch (error) {
    console.error('getImportFilesHistoryAction error:', error);
    return false;
  } finally {
    if (runDataPipeline) {
      messageSuccess('Ejecución del pipeline iniciada, esto puede tardar unos minutos.');
      setTimeout(async () => {
        await runDataPipelineAPI({ token });
      }, 3000);
    }
  }
}

export async function handlePaginationAction({
  dispatch,
  page,
  pageSize,
  dataIntegrationState,
  token,
  translate,
}: {
  dispatch: Dispatch<any>;
  page: number;
  pageSize: number;
  dataIntegrationState: IInitialDataIntegrationState;
  token: string;
  translate: ITranslate;
}) {
  dataIntegrationState.importFilesHistoryBody.index =
    page === 1 ? 0 : page * pageSize - pageSize;
  dataIntegrationState.importFilesHistoryBody.limit = pageSize;
  const getHistory = await getUploadsHistory({
    dispatch,
    importFilesHistoryBody: dataIntegrationState.importFilesHistoryBody,
    token,
  });
  if (!getHistory) {
    notificationErr(dataIntegrationError.getUploadsHistory_001, translate);
    return false;
  }
  return true;
}

//Audiences /////////////////////////////////////////////////////////////////
export async function getAudiencesAction({
  dispatch,
  token,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_GET_AUDIENCES, payload: true });
    const getAudiences = await getAudiencesAPI({ token });
    if (getAudiences.response.status === 200) {
      dispatch({ type: types.SET_AUDIENCES, payload: getAudiences.data });
      return true;
    }
    return false;
  } catch (error) {
    notificationErr(dataIntegrationError.getAudiences_001, translate);
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_GET_AUDIENCES, payload: false });
  }
}

export async function createAudienceAction({
  dispatch,
  token,
  body,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  token: string;
  body: IcreateAudienceAPIBody;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_CREATE_AUDIENCE, payload: true });
    const createAudience = await createAudienceAPI({ token, body: [body] });
    if (
      createAudience.response.status === 200 ||
      createAudience.response.status === 201
    ) {
      dispatch({ type: types.CREATE_AUDIENCE, payload: createAudience.data[0] });
      messageSuccess('Audiencia creada con éxito');
      return true;
    }
    notificationErr(dataIntegrationError.createAudience_002, translate);
    messageError('No se ha podido crear la audiencia');
    return false;
  } catch (error) {
    notificationErr(dataIntegrationError.createAudience_001, translate);
    messageError('No se ha podido crear la audiencia');
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_CREATE_AUDIENCE, payload: false });
  }
}

export async function updateAudienceAction({
  dispatch,
  token,
  body,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  token: string;
  body: IupdateAudienceAPIBody;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_UPDATE_AUDIENCE, payload: true });
    const updateAudience = await updateAudienceAPI({ token, body });
    if (
      updateAudience.response.status === 200 ||
      updateAudience.response.status === 201
    ) {
      dispatch({
        type: types.UPDATE_AUDIENCE,
        payload: { id: body.filter.id, audience: body.update },
      });
      messageSuccess('Audiencia actualizada con éxito');
      return true;
    }
    notificationErr(dataIntegrationError.updateAudience_002, translate);
    messageError('No se ha podido actualizar la audiencia');
    return false;
  } catch (error) {
    messageError('No se ha podido actualizar la audiencia');
    notificationErr(dataIntegrationError.updateAudience_001, translate);
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_UPDATE_AUDIENCE, payload: false });
  }
}

// Mapping Fields ///////////////////////////////////////////////////////////////
export async function getMappingFieldsAction({
  dispatch,
  token,
  audience,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  token: string;
  audience: string | null;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_GET_MAPPING_FIELDS, payload: true });
    const getMappingFields = await getMappingFieldsAPI({ token, audienceId: audience });
    if (getMappingFields.response.status === 200) {
      dispatch({ type: types.SET_MAPPING_FIELDS, payload: getMappingFields.data });
      dispatch({ type: types.SET_MAPPING_FIELDS_AUDIENCE_SELECTED, payload: audience });
      return true;
    }
    return false;
  } catch (error) {
    notificationErr(dataIntegrationError.getMappingFields_001, translate);
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_GET_MAPPING_FIELDS, payload: false });
  }
}

export async function createMappingFieldAction({
  dispatch,
  token,
  body,
  mappingFieldsAudienceSelected,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  token: string;
  body: IcreateMappingFieldAPIBodyItem;
  mappingFieldsAudienceSelected: string | null;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_CREATE_MAPPING_FIELD, payload: true });
    const createMappingField = await createMappingFieldAPI({ token, body: [body] });
    if (
      createMappingField.response.status === 200 ||
      createMappingField.response.status === 201
    ) {
      if (
        mappingFieldsAudienceSelected === body.audience_id ||
        !mappingFieldsAudienceSelected
      ) {
        dispatch({
          type: types.CREATE_MAPPING_FIELD,
          payload: createMappingField.data[0],
        });
      }
      messageSuccess('Campo de mapeo creado con éxito');
      return true;
    }
    notificationErr(dataIntegrationError.createMappingField_002, translate);
    messageError('No se ha podido crear el campo de mapeo');
    return false;
  } catch (error) {
    notificationErr(dataIntegrationError.createMappingField_001, translate);
    messageError('No se ha podido crear el campo de mapeo');
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_CREATE_MAPPING_FIELD, payload: false });
  }
}

export async function updateMappingFieldAction({
  dispatch,
  token,
  body,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  token: string;
  body: IupdateMappingFieldAPIBody;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_UPDATE_MAPPING_FIELD, payload: true });
    const updateMappingField = await updateMappingFieldAPI({ token, body });
    if (
      updateMappingField.response.status === 200 ||
      updateMappingField.response.status === 201
    ) {
      dispatch({
        type: types.UPDATE_MAPPING_FIELD,
        payload: { id: body.filter.id, mappingField: body.update },
      });
      messageSuccess('Campo de mapeo actualizado con éxito');
      return true;
    }
    notificationErr(dataIntegrationError.updateMappingField_002, translate);
    messageError('No se ha podido actualizar el campo de mapeo');
    return false;
  } catch (error) {
    messageError('No se ha podido actualizar el campo de mapeo');
    notificationErr(dataIntegrationError.updateMappingField_001, translate);
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_UPDATE_MAPPING_FIELD, payload: false });
  }
}

export async function deleteMappingFieldAction({
  dispatch,
  token,
  mappingFieldId,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  token: string;
  mappingFieldId: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({
      type: types.SET_IS_LOADING_DELETE_MAPPING_FIELD,
      payload: { mappingFieldId, isLoading: true },
    });
    const deleteMappingField = await deleteMappingFieldAPI({ token, mappingFieldId });
    if (
      deleteMappingField.response.status === 200 ||
      deleteMappingField.response.status === 201
    ) {
      dispatch({ type: types.DELETE_MAPPING_FIELD, payload: { id: mappingFieldId } });
      messageSuccess('Campo de mapeo eliminado con éxito');
      return true;
    }
    notificationErr(dataIntegrationError.deleteMappingField_002, translate);
    messageError('No se ha podido eliminar el campo de mapeo');
    return false;
  } catch (error) {
    messageError('No se ha podido eliminar el campo de mapeo');
    notificationErr(dataIntegrationError.deleteMappingField_001, translate);
    return false;
  } finally {
    dispatch({
      type: types.SET_IS_LOADING_DELETE_MAPPING_FIELD,
      payload: { mappingFieldId: null, isLoading: false },
    });
  }
}

// Mappings /////////////////////////////////////////////////////////////////
export async function getMappingsAction({
  dispatch,
  brandId,
  token,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  brandId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_GET_MAPPINGS, payload: true });
    const getMappings = await getMappingsAPI({ token, brandId: brandId });
    if (getMappings.response.status === 200) {
      dispatch({ type: types.SET_MAPPINGS, payload: getMappings.data });
      return true;
    }
    return false;
  } catch (error) {
    notificationErr(dataIntegrationError.getMappings_001, translate);
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_GET_MAPPINGS, payload: false });
  }
}

export async function createMappingAction({
  dispatch,
  token,
  body,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  token: string;
  body: IcreateMappingAPIBody;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_CREATE_MAPPING, payload: true });
    const createMapping = await createMappingAPI({ token, body });
    if (createMapping.response.status === 200) {
      dispatch({ type: types.CREATE_MAPPING, payload: createMapping.data });
      messageSuccess('Mapa creado con éxito');
      return true;
    }
    if (createMapping.response.status === 403) {
      messageWarning('Ya existe un mapa con la misma firma');
      return false;
    }
    notificationErr(dataIntegrationError.createMapping_002, translate);
    messageError('No se ha podido crear el mapa');
    return false;
  } catch (error) {
    notificationErr(dataIntegrationError.createMapping_001, translate);
    messageError('No se ha podido crear el mapa');
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_CREATE_MAPPING, payload: false });
    // setTimeout(async () => {
    //   await runDataPipelineAPI({ token });
    // }, 3000);
  }
}

export async function updateMappingAction({
  dispatch,
  token,
  body,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  token: string;
  body: IupdateMappingAPIBody;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_UPDATE_MAPPING, payload: true });
    const updateMapping = await updateMappingAPI({ token, body });
    if (updateMapping.response.status === 200) {
      dispatch({
        type: types.UPDATE_MAPPING,
        payload: { id: body.filter.id, mapping: body.update },
      });
      messageSuccess('Mapa actualizado con éxito');
      return true;
    }
    notificationErr(dataIntegrationError.updateMappingField_002, translate);
    messageError('No se ha podido actualizar el mapa');
    return false;
  } catch (error) {
    messageError('No se ha podido actualizar el mapa');
    notificationErr(dataIntegrationError.updateMappingField_001, translate);
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_UPDATE_MAPPING, payload: false });
    // setTimeout(async () => {
    //   await runDataPipelineAPI({ token });
    // }, 3000);
  }
}

export async function deleteMapAction({
  dispatch,
  token,
  mapId,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  token: string;
  mapId: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({
      type: types.SET_IS_LOADING_DELETE_MAP,
      payload: { mapId, isLoading: true },
    });
    const deleteMap = await deleteMapAPI({ token, mapId });
    if (deleteMap.response.status === 200) {
      dispatch({ type: types.DELETE_MAP, payload: { id: mapId } });
      messageSuccess('Mapa eliminado con éxito');
      return true;
    }
    notificationErr(dataIntegrationError.deleteMap_002, translate);
    messageError('No se ha podido eliminar el mapa');
    return false;
  } catch (error) {
    messageError('No se ha podido eliminar el mapeado');
    notificationErr(dataIntegrationError.deleteMap_001, translate);
    return false;
  } finally {
    dispatch({
      type: types.SET_IS_LOADING_DELETE_MAP,
      payload: { mapId: null, isLoading: false },
    });
  }
}

export async function setCSVSelectedAction({
  dispatch,
  csvData,
  file,
}: {
  dispatch: React.Dispatch<TdataIntegrationDispatchReducer>;
  csvData: Papa.ParseResult<unknown> | null;
  file: File | null | undefined;
}): Promise<boolean> {
  dispatch({
    type: types.SET_CSV_SELECTED,
    payload: { papa: csvData, file: file },
  });
  dispatch({
    type: types.SET_IS_LOADING_CSV_SELECTED,
    payload: false,
  });
  return true;
}

// Points of Sale Dictionary /////////////////////////////////////////////////////////////////
export async function getPointsOfSaleDictionaryAction({
  dispatch,
  companyId,
  token,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  companyId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_GET_POINTS_OF_SALE_DICTIONARY, payload: true });
    const getPointsOfSaleDictionary = await getPointsOfSaleDictionaryAPI({
      token,
      clientId: companyId,
    });
    if (getPointsOfSaleDictionary.response.status === 200) {
      dispatch({
        type: types.SET_POINTS_OF_SALE_DICTIONARY,
        payload: getPointsOfSaleDictionary.data,
      });
      return true;
    }
    return false;
  } catch (error) {
    notificationErr(dataIntegrationError.getPointsOfSaleDictionary_001, translate);
    return false;
  } finally {
    dispatch({
      type: types.SET_IS_LOADING_GET_POINTS_OF_SALE_DICTIONARY,
      payload: false,
    });
  }
}

export async function createPointsOfSaleDictionaryAction({
  dispatch,
  token,
  body,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  token: string;
  body: IcreatePointsOfSaleDictionaryAPIBodyItem;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({
      type: types.SET_IS_LOADING_CREATE_POINTS_OF_SALE_DICTIONARY,
      payload: true,
    });
    const createPointsOfSaleDictionary = await createPointsOfSaleDictionaryAPI({
      token,
      body: [body],
    });
    if (
      (createPointsOfSaleDictionary.response.status === 200 ||
        createPointsOfSaleDictionary.response.status === 201) &&
      createPointsOfSaleDictionary.data.created.length > 0
    ) {
      dispatch({
        type: types.CREATE_POINTS_OF_SALE_DICTIONARY,
        payload: createPointsOfSaleDictionary.data.created[0],
      });
      messageSuccess('Diccionario de punto de venta creado con éxito');
      return true;
    }
    if (
      (createPointsOfSaleDictionary.response.status === 200 ||
        createPointsOfSaleDictionary.response.status === 201) &&
      createPointsOfSaleDictionary.data.duplicated.length > 0
    ) {
      messageError('El punto de venta ya existe en el diccionario');
      return false;
    }
    notificationErr(dataIntegrationError.createPointsOfSaleDictionary_002, translate);
    messageError('No se ha podido crear el diccionario de punto de venta');
    return false;
  } catch (error) {
    notificationErr(dataIntegrationError.createPointsOfSaleDictionary_001, translate);
    messageError('No se ha podido crear el diccionario de punto de venta');
    return false;
  } finally {
    dispatch({
      type: types.SET_IS_LOADING_CREATE_POINTS_OF_SALE_DICTIONARY,
      payload: false,
    });
  }
}

export async function updatePointsOfSaleDictionaryAction({
  dispatch,
  token,
  body,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  token: string;
  body: IupdatePointsOfSaleDictionaryAPIBody;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({
      type: types.SET_IS_LOADING_UPDATE_POINTS_OF_SALE_DICTIONARY,
      payload: true,
    });
    const updatePointsOfSaleDictionary = await updatePointsOfSaleDictionaryAPI({
      token,
      body,
    });
    if (
      updatePointsOfSaleDictionary.response.status === 200 ||
      updatePointsOfSaleDictionary.response.status === 201
    ) {
      dispatch({
        type: types.UPDATE_POINTS_OF_SALE_DICTIONARY,
        payload: { id: body.filter.id, pointsOfSaleDictionary: body.update },
      });
      messageSuccess('Diccionario de punto de venta actualizado con éxito');
      return true;
    }
    notificationErr(dataIntegrationError.updateMappingField_002, translate);
    messageError('No se ha podido actualizar el diccionario de punto de venta');
    return false;
  } catch (error) {
    messageError('No se ha podido actualizar el diccionario de punto de venta');
    notificationErr(dataIntegrationError.updateMappingField_001, translate);
    return false;
  } finally {
    dispatch({
      type: types.SET_IS_LOADING_UPDATE_POINTS_OF_SALE_DICTIONARY,
      payload: false,
    });
  }
}

export async function deletePointsOfSaleDictionaryAction({
  dispatch,
  token,
  pointOfSaleDictionaryId,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  token: string;
  pointOfSaleDictionaryId: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({
      type: types.SET_IS_LOADING_DELETE_POINTS_OF_SALE_DICTIONARY,
      payload: { pointOfSaleDictionaryId, isLoading: true },
    });
    const deletePointsOfSaleDictionary = await deletePointsOfSaleDictionaryAPI({
      token,
      pointOfSaleDictionaryId,
    });
    if (
      deletePointsOfSaleDictionary.response.status === 200 ||
      deletePointsOfSaleDictionary.response.status === 201
    ) {
      dispatch({
        type: types.DELETE_POINTS_OF_SALE_DICTIONARY,
        payload: { id: pointOfSaleDictionaryId },
      });
      messageSuccess('Diccionario de punto de venta eliminado con éxito');
      return true;
    }
    notificationErr(dataIntegrationError.updateMappingField_002, translate);
    messageError('No se ha podido eliminar el diccionario de punto de venta');
    return false;
  } catch (error) {
    messageError('No se ha podido eliminar el diccionario de punto de venta');
    notificationErr(dataIntegrationError.updateMappingField_001, translate);
    return false;
  } finally {
    dispatch({
      type: types.SET_IS_LOADING_DELETE_POINTS_OF_SALE_DICTIONARY,
      payload: { pointOfSaleDictionaryId: null, isLoading: false },
    });
  }
}

// Files Patterns /////////////////////////////////////////////////////////////////
export async function getFilePatternsAction({
  dispatch,
  companyId,
  token,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  companyId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_GET_FILE_PATTERNS, payload: true });
    const getFilePatterns = await getFilePatternsAPI({ clientId: companyId, token });
    if (getFilePatterns.response.status === 200) {
      dispatch({ type: types.SET_FILE_PATTERNS, payload: getFilePatterns.data });
      return true;
    }
    return false;
  } catch (error) {
    notificationErr(dataIntegrationError.getFilePatterns_001, translate);
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_GET_FILE_PATTERNS, payload: false });
  }
}

export async function createFilePatternAction({
  dispatch,
  token,
  body,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  token: string;
  body: IcreateFilePatternAPIBody;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_CREATE_FILE_PATTERN, payload: true });
    const createFilePattern = await createFilePatternAPI({
      token,
      body,
    });
    if (
      createFilePattern.response.status === 200 ||
      createFilePattern.response.status === 201
    ) {
      dispatch({
        type: types.CREATE_FILE_PATTERN,
        payload: createFilePattern.data[0],
      });
      messageSuccess('Patrón de fichero creado con éxito');
      return true;
    }
    notificationErr(dataIntegrationError.createFilePattern_002, translate);
    messageError('No se ha podido crear el patrón de fichero');
    return false;
  } catch (error) {
    notificationErr(dataIntegrationError.createFilePattern_001, translate);
    messageError('No se ha podido crear el patrón de fichero');
    return false;
  } finally {
    dispatch({
      type: types.SET_IS_LOADING_CREATE_FILE_PATTERN,
      payload: false,
    });
  }
}

// Upload Files /////////////////////////////////////////////////////////////////
export async function uploadFilesAction({
  dispatch,
  token,
  body,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  token: string;
  body: Array<IuploadFilesAPIBodyItem>;
  translate: ITranslate;
}): Promise<{
  ok: boolean;
  confirmedItems: Array<IuploadFilesAPIBodyItem>;
}> {
  let uploadedConfirmedFiles: Array<IuploadFilesAPIBodyItem> = [];
  try {
    dispatch({ type: types.SET_IS_LOADING_UPLOAD_FILES, payload: true });
    const uploadFilesBody: IuploadFilesAPIBodyItem[] = body.map((file) => {
      if (file.encoding) {
        return {
          name: file.name,
          brand_id: file.brand_id,
          audience_id: file.audience_id,
          content_type: file.content_type,
          encoding: file.encoding,
        };
      } else {
        return {
          name: file.name,
          brand_id: file.brand_id,
          audience_id: file.audience_id,
          content_type: file.content_type,
        };
      }
    });
    const uploadFiles = await uploadFilesAPI({ token, body: uploadFilesBody });
    if (uploadFiles.response.status === 200) {
      await Promise.all(
        Object.keys(uploadFiles.data).map(async (item) => {
          const uploadFileToS3 = await uploadFileToS3API({
            file: body.find((file) => file.name === item)?.file as File,
            contentEncoding: uploadFiles.data[item].new_file.file_encoding ?? '',
            presigned_url: uploadFiles.data[item].presigned_url,
          });
          if (uploadFileToS3.ok) {
            const confirmUploadFile = await confirmUploadFileAPI({
              key: uploadFiles.data[item].key,
              token,
            });
            if (confirmUploadFile.response.status !== 200) {
              messageError(`Error al confirmar el fichero ${item} en la API`, 15);
            }
            uploadedConfirmedFiles.push(
              body.find((file) => file.name === item) as IuploadFilesAPIBodyItem
            );
          } else {
            messageError(`Error al subir el fichero ${item}`, 15);
          }
        })
      );
      if (uploadedConfirmedFiles.length === 0) {
        messageWarning('No se han subido ficheros', 15);
        return {
          ok: false,
          confirmedItems: [],
        };
      }
      if (uploadedConfirmedFiles.length === body.length) {
        messageSuccess('Ficheros subidos con éxito');
        return {
          ok: true,
          confirmedItems: uploadedConfirmedFiles,
        };
      }
      messageWarning('Algunos ficheros no se han podido subir', 15);
      return {
        ok: false,
        confirmedItems: [],
      };
    }
    messageError('No se ha podido subir los ficheros');
    notificationErr(dataIntegrationError.uploadFiles_002, translate);
    return {
      ok: false,
      confirmedItems: [],
    };
  } catch (error) {
    notificationErr(dataIntegrationError.uploadFiles_001, translate);
    messageError('No se ha podido subir los ficheros');
    return {
      ok: false,
      confirmedItems: [],
    };
  } finally {
    dispatch({ type: types.SET_IS_LOADING_UPLOAD_FILES, payload: false });
    if (uploadedConfirmedFiles.length > 0) {
      // setTimeout(async () => {
      //   await runDataPipelineAPI({ token });
      // }, 3000);
    }
  }
}

export async function reprocessImportAction({
  dispatch,
  companyId,
  importFilesHistoryBody,
  token,
  importIds,
  translate,
  reprocessAll,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  companyId: string;
  importFilesHistoryBody: IgetHistoryAPIBody;
  token: string;
  importIds: string[];
  translate: ITranslate;
  reprocessAll: boolean;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_REPROCESS_IMPORT, payload: true });

    let reprocessImportSuccess = 0;

    if (reprocessAll) {
      let body: IgetHistoryAPIBody = initialUploadsHistoryBody;
      body.brand_id = companyId;
      body.state = 'mapping_error';

      const getUploadsHistory = await getHistoryAPI({
        body,
        token,
      });
      if (
        getUploadsHistory.response.status === 200 &&
        getUploadsHistory.data.data.length > 0
      ) {
        const allIdsItems = getUploadsHistory.data.data.map(
          (item) => item.validations.id
        );
        for (const id of allIdsItems) {
          const reprocessImport = await reprocessImportAPI({ token, importId: id });
          if (reprocessImport.response.status === 200) {
            reprocessImportSuccess += 1;
          }
        }
        if (reprocessImportSuccess === allIdsItems.length) {
          messageSuccess('Reprocesamiento del fichero iniciado con éxito');
          return true;
        }
      }
      if (getUploadsHistory.response.status === 404) {
        messageWarning('No hay ficheros para reprocesar');
        return true;
      }
    } else {
      for (const id of importIds) {
        const reprocessImport = await reprocessImportAPI({ token, importId: id });
        if (reprocessImport.response.status === 200) {
          reprocessImportSuccess += 1;
        }
      }
      if (reprocessImportSuccess === importIds.length) {
        messageSuccess('Reprocesamiento del fichero iniciado con éxito');
        return true;
      }
    }
    notificationErr(dataIntegrationError.reprocessImport_002, translate);
    messageError('No se ha podido iniciar el reprocesamiento de todos los ficheros');
    return false;
  } catch (error) {
    console.log({ error });
    notificationErr(dataIntegrationError.reprocessImport_001, translate);
    messageError('No se ha podido iniciar el reprocesamiento del fichero');
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_REPROCESS_IMPORT, payload: false });
    // setTimeout(async () => {
    //   await runDataPipelineAPI({ token });
    // }, 3000);
    // delete importFilesHistoryBody.state;
    await getUploadsHistory({
      dispatch,
      importFilesHistoryBody,
      token,
    });
  }
}

////////////////////////////////////////////////////////////////////////////////////////
async function getUploadsHistory({
  dispatch,
  importFilesHistoryBody,
  token,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  importFilesHistoryBody: IgetHistoryAPIBody;
  token: string;
}) {
  try {
    dispatch({ type: types.SET_IS_LOADING_GET_IMPORT_FILES_HISTORY, payload: true });
    const getUploadsHistory = await getHistoryAPI({
      body: importFilesHistoryBody,
      token,
    });

    if (getUploadsHistory.response.status === 404) {
      dispatch({
        type: types.SET_IMPORT_FILES_HISTORY,
        payload: {
          history: {
            data: [],
            total: 0,
            count: 0,
          },
          body: importFilesHistoryBody,
        },
      });
      return true;
    }

    if (getUploadsHistory.response.status === 200) {
      let copyOfData: IgetHistoryAPIResponseData = JSON.parse(
        JSON.stringify(getUploadsHistory.data)
      );
      copyOfData.data = copyOfData.data.map((item) => {
        return {
          ...item,
          key: item.id,
        };
      });
      dispatch({
        type: types.SET_IMPORT_FILES_HISTORY,
        payload: { history: copyOfData, body: importFilesHistoryBody },
      });
      return true;
    }
    return false;
  } catch (error) {
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_GET_IMPORT_FILES_HISTORY, payload: false });
  }
}

export async function updateInboxFileEncodingAction({
  token,
  inboxId,
  fileEncoding,
  translate,
}: {
  token: string;
  inboxId: string;
  fileEncoding: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    const updateInboxFileEncoding = await updateInboxFileEncodingAPI({
      token,
      inboxId,
      fileEncoding,
    });
    if (updateInboxFileEncoding.response.status !== 200) {
      notificationErr(dataIntegrationError.updateInboxFileEncoding_002, translate);
      return false;
    }
    messageSuccess('Codificación del fichero actualizada con éxito');
    return true;
  } catch (error) {
    notificationErr(dataIntegrationError.updateInboxFileEncoding_001, translate);
    return false;
  }
}

export async function archiveFileHistoryAction({
  dispatch,
  importFilesHistoryBody,
  importHistoryIds,
  token,
  translate,
}: {
  dispatch: Dispatch<TdataIntegrationDispatchReducer>;
  importFilesHistoryBody: IgetHistoryAPIBody;
  importHistoryIds: string[];
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({
      type: types.SET_IS_LOADING_ARCHIVE_FILE_HISTORY,
      payload: {
        importHistoryIds: importHistoryIds,
        isLoading: true,
      },
    });
    let archiveFileHistorySuccess = 0;
    for (const id of importHistoryIds) {
      const archiveFileHistory = await archiveFileHistoryAPI({ token, importId: id });
      if (archiveFileHistory.response.status === 200) {
        archiveFileHistorySuccess += 1;
      }
    }
    if (archiveFileHistorySuccess === importHistoryIds.length) {
      messageSuccess(`${archiveFileHistorySuccess} fichero/s archivado correctamente`);
      await getUploadsHistory({
        dispatch,
        importFilesHistoryBody,
        token,
      });
      return true;
    }
    notificationErr(dataIntegrationError.archiveFileHistory_002, translate);
    messageError('No se ha podido archivar todos los ficheros');
    return false;
  } catch (error) {
    console.log({ error });
    notificationErr(dataIntegrationError.archiveFileHistory_001, translate);
    messageError('No se ha podido archivar los ficheros');
    return false;
  } finally {
    dispatch({
      type: types.SET_IS_LOADING_ARCHIVE_FILE_HISTORY,
      payload: {
        importHistoryIds: null,
        isLoading: false,
      },
    });
  }
}
